import { type CanvasSidebarStartUpdateEventDetail, PButton, PCanvas } from '@porsche-design-system/components-react';
import { ErrorBoundary, ErrorBoundaryProps } from '@sentry/react';
import { Suspense, useCallback, useId, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { handleError } from '../../blocks/Error';
import Loader from '../../common/Loader';
import { createHeader } from '../useHeader';
import SideMenu, { UserNavigationItem } from './SideMenu';
import useLayoutStyle, { SidebarStartSlot } from './useLayoutStyle';

const beforeCapture: ErrorBoundaryProps['beforeCapture'] = scope => {
    scope.setTag('boundary', 'DashboardLayout');
};

export type StackedLayoutProps = {
    userNavigation: Array<UserNavigationItem>;
};

const StackedLayout = ({ userNavigation }: StackedLayoutProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    useLayoutStyle(wrapperRef);
    const headerId = useId();
    const header = createHeader(headerId, '');

    const [isSidebarStartOpen, setIsSidebarStartOpen] = useState<boolean>(false);
    const onSidebarStartUpdate = useCallback((e: CustomEvent<CanvasSidebarStartUpdateEventDetail>) => {
        setIsSidebarStartOpen(e.detail.open);
    }, []);

    const navigate = useNavigate();
    const onHeaderEndClick = useCallback(() => {
        navigate('/profile');
    }, [navigate]);

    return (
        <div ref={wrapperRef}>
            <PCanvas
                inert={undefined}
                onSidebarStartUpdate={onSidebarStartUpdate}
                sidebarEndOpen={false}
                sidebarStartOpen={isSidebarStartOpen}
            >
                <div slot="title" style={{ height: '20px' }} />
                <PButton
                    icon="user"
                    inert={undefined}
                    onClick={onHeaderEndClick}
                    slot="header-end"
                    type="button"
                    variant="ghost"
                    compact
                    hideLabel
                />

                <SidebarStartSlot slot="sidebar-start">
                    <SideMenu userNavigation={userNavigation} />
                </SidebarStartSlot>

                <div style={{ containerType: 'inline-size' }}>
                    <div id={headerId} />

                    <main>
                        <ErrorBoundary beforeCapture={beforeCapture} fallback={handleError}>
                            <Suspense fallback={<Loader />}>{header.render(<Outlet />)}</Suspense>
                        </ErrorBoundary>
                    </main>
                </div>
            </PCanvas>
        </div>
    );
};

export default StackedLayout;
